import {gql} from '@apollo/client';
import {areSetsEqual, naturalSortByKey} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useEffect, useState} from 'react';

import {bulkArchiveStudySchemas, bulkUnarchiveStudySchemas} from 'archive/ArchiveActions';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import useAreOrganizationSchemasCreatableQuery from 'auth/useAreOrganizationSchemasCreatableQuery';
import * as benchlingUtil from 'BenchlingUtil';
import {ActionOrigin, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import SchemasListing from 'schemas/SchemasListing';
import {SCHEMA_TYPES} from 'schemas/spec/SchemaConfigConstants';
import {StudySchemasPageQuery} from 'studies/study-schemas/__generated/StudySchemasPageQuery';
import {getCreateStudySchemaURL, getStudySchemaURL} from 'studies/study-schemas/StudySchemaDAO';
import {getStudyTypeText} from 'studies/TypedStudyUtils';

import {StudyTypeOption, StudyTypeSelector} from './StudyTypeSelector';

type StudySchema = StudySchemasPageQuery['studySchemas'][number];

interface ListedStudyTypeFilterProps {
  onChangeStudyType: (studyType: StudyTypeOption | null) => void;
  schemas: Array<StudySchema> | null;
  selectedStudyType: StudyTypeOption | null;
}

function ListedStudyTypeFilter({
  onChangeStudyType,
  schemas,
  selectedStudyType,
}: ListedStudyTypeFilterProps): JSX.Element {
  const nextStudyTypes = schemas == null ? null : new Set(schemas.map((schema) => schema.studyType));
  const [studyTypes, setStudyTypes] = useState<Set<StudyType>>(() => nextStudyTypes ?? new Set());
  if (nextStudyTypes != null && !areSetsEqual(studyTypes, nextStudyTypes)) {
    setStudyTypes(nextStudyTypes);
  }

  return (
    <div className={css(styles.filterContainer)}>
      <StudyTypeSelector
        displayName={selectedStudyType ? getStudyTypeText(selectedStudyType.studyType) : 'All'}
        onChangeStudyType={onChangeStudyType}
        selectedStudyType={selectedStudyType}
        studyTypes={Array.from(studyTypes)}
      />
    </div>
  );
}

interface StudySchemasPageProps {
  schemas: Array<StudySchema> | null;
  errorMessage?: string;
  areOrganizationSchemasCreatable: boolean;
  refetch: () => void;
}

export const StudySchemasPage = (props: StudySchemasPageProps): JSX.Element => {
  const [selectedStudyType, setSelectedStudyType] = useState<StudyTypeOption | null>(null);

  const handleArchiveSchemas = async (ids: Array<string>, purpose: string): Promise<void> => {
    await bulkArchiveStudySchemas(ids, purpose, ActionOrigin.SETTINGS_STUDY_SCHEMA_PAGE);
    props.refetch();
  };

  const handleUnarchiveSchemas = async (ids: Array<string>): Promise<void> => {
    await bulkUnarchiveStudySchemas(ids, ActionOrigin.SETTINGS_STUDY_SCHEMA_PAGE);
    props.refetch();
  };

  useEffect(() => {
    benchlingUtil.setWindowTitle('Study Schemas');
  });

  const customFilterCallback = (schemas: Array<StudySchema>): Array<StudySchema> => {
    if (!selectedStudyType) {
      return schemas;
    }

    return schemas.filter((schema) => schema.studyType === selectedStudyType.studyType);
  };

  const mapSchemaToRowValue = (schema: StudySchema): Array<JSX.Element | string> => [
    <span className="u-small-spacing-left">
      {schema.name}
      <ArchiveRecordLabel item={schema} />
    </span>,
    getStudyTypeText(schema.studyType),
  ];

  const handleStudyTypeChange = (studyType: StudyTypeOption | null): void => {
    setSelectedStudyType(studyType);
  };

  const renderStudyTypeFilter = (schemas: Array<StudySchema> | null): JSX.Element => (
    <ListedStudyTypeFilter
      onChangeStudyType={handleStudyTypeChange}
      schemas={schemas}
      selectedStudyType={selectedStudyType}
    />
  );

  return (
    <SchemasListing
      schemas={props.schemas}
      errorMessage={props.errorMessage}
      schemaType={SCHEMA_TYPES.STUDY}
      navigateToCreate={() => {
        benchlingUtil.navigate(null, getCreateStudySchemaURL());
      }}
      urlForSchema={getStudySchemaURL}
      archiveSchemas={handleArchiveSchemas}
      unarchiveSchemas={handleUnarchiveSchemas}
      isWritable={props.areOrganizationSchemasCreatable}
      customFilterCallback={customFilterCallback}
      customSearchControls={renderStudyTypeFilter}
      columnHeaders={[
        {name: 'Schemas', width: 9},
        {name: 'Study Type', width: 3},
      ]}
      mapSchemaToRowValue={mapSchemaToRowValue}
    />
  );
};

export const STUDY_SCHEMAS_PAGE_QUERY: DocumentNode = gql`
  query StudySchemasPageQuery @skipAudit @engComponent(name: STUDIES) {
    studySchemas {
      id
      name
      studyType
      archiveRecord {
        purpose
      }
    }
  }
`;

export default function ConnectedStudySchemasPage(): JSX.Element {
  const areOrganizationSchemasCreatableResults = useAreOrganizationSchemasCreatableQuery();
  const areOrganizationSchemasCreatable =
    areOrganizationSchemasCreatableResults.queryResultType === QueryResultType.SUCCESS &&
    areOrganizationSchemasCreatableResults.areOrganizationSchemasCreatable;
  const queryResult = useBenchlingQuery<StudySchemasPageQuery>(STUDY_SCHEMAS_PAGE_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const studySchemas =
    queryResult.type === QueryResultType.SUCCESS
      ? naturalSortByKey(queryResult.data.studySchemas, 'name')
      : null;

  return (
    <StudySchemasPage
      schemas={studySchemas}
      errorMessage={queryResult.type === QueryResultType.ERROR ? 'Error loading study schemas' : undefined}
      areOrganizationSchemasCreatable={areOrganizationSchemasCreatable}
      refetch={queryResult.refetch}
    />
  );
}

const styles = StyleSheet.create({
  filterContainer: {
    'min-width': '100px',
  },
});
